/**
 * The signed-in person's own settings: their name, their language, their password, two-factor
 * sign-in and deleting the account. Business settings
 * (members, categories, the inbox) live in /settings; this page is about the person.
 */
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import { DeleteAccountCard } from "@/components/settings/delete-account";
import { TwoFactorSettings } from "@/components/settings/two-factor-settings";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { isLocale, LOCALE_NAMES, LOCALES } from "@/i18n";
import { changePassword, isWrongOldPassword, updateName } from "@/lib/api/profile";
import { useAuth } from "@/lib/session/auth-context";
import { applyServerErrors } from "@/lib/utils/form-errors";

export function ProfilePage() {
  const { t, i18n } = useTranslation();
  const { user, setLocale } = useAuth();
  const locale = isLocale(i18n.resolvedLanguage) ? i18n.resolvedLanguage : "en";
  const [savingName, setSavingName] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  // Schemas inside the component so the messages follow the language.
  const nameSchema = z.object({ name: z.string().trim().min(2, t("validation.name")).max(100) });
  const passwordSchema = z
    .object({
      oldPassword: z.string().min(1, t("profile.oldPasswordRequired")),
      password: z.string().min(8, t("validation.password")),
      passwordConfirm: z.string(),
    })
    .refine((values) => values.password === values.passwordConfirm, {
      message: t("profile.passwordMismatch"),
      path: ["passwordConfirm"],
    })
    .refine((values) => values.password !== values.oldPassword, {
      message: t("profile.passwordUnchanged"),
      path: ["password"],
    });
  type NameValues = z.infer<typeof nameSchema>;
  type PasswordValues = z.infer<typeof passwordSchema>;

  const nameForm = useForm<NameValues>({
    resolver: zodResolver(nameSchema),
    values: { name: user?.name ?? "" },
  });
  const passwordForm = useForm<PasswordValues>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { oldPassword: "", password: "", passwordConfirm: "" },
  });

  if (!user) return null;

  async function saveName(values: NameValues) {
    setSavingName(true);
    try {
      await updateName(user!.id, values.name);
      toast.success(t("profile.nameSaved"));
    } catch (error) {
      const message = applyServerErrors(error, (name, err) =>
        nameForm.setError(name as keyof NameValues, err),
      );
      if (message) toast.error(message);
    } finally {
      setSavingName(false);
    }
  }

  async function savePassword(values: PasswordValues) {
    setSavingPassword(true);
    try {
      await changePassword(user!, { oldPassword: values.oldPassword, password: values.password });
      passwordForm.reset();
      toast.success(t("profile.passwordSaved"));
    } catch (error) {
      if (isWrongOldPassword(error)) {
        passwordForm.setError("oldPassword", { message: t("profile.wrongOldPassword") });
      } else {
        const message = applyServerErrors(error, (name, err) =>
          passwordForm.setError(name as keyof PasswordValues, err),
        );
        if (message) toast.error(message);
      }
    } finally {
      setSavingPassword(false);
    }
  }

  return (
    <div className="mx-auto max-w-xl space-y-6">
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">{t("profile.title")}</h1>
        <p className="text-sm text-muted-foreground">{t("profile.subtitle")}</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t("profile.personal")}</CardTitle>
        </CardHeader>
        <CardContent>
          <Form {...nameForm}>
            <form
              data-testid="profile-name-form"
              className="space-y-4"
              onSubmit={nameForm.handleSubmit(saveName)}
            >
              <FormField
                control={nameForm.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("profile.name")}</FormLabel>
                    <FormControl>
                      <Input autoComplete="name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="space-y-2">
                <Label htmlFor="profile-email">{t("profile.email")}</Label>
                <Input id="profile-email" value={user.email} readOnly disabled />
                <p className="text-sm text-muted-foreground">{t("profile.emailHint")}</p>
              </div>
              <Button type="submit" disabled={savingName}>
                {t("profile.saveName")}
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("profile.language")}</CardTitle>
          <CardDescription>{t("profile.languageHint")}</CardDescription>
        </CardHeader>
        <CardContent>
          <Select
            value={locale}
            onValueChange={(value) => isLocale(value) && void setLocale(value)}
          >
            <SelectTrigger
              className="w-full sm:w-64"
              aria-label={t("profile.language")}
              data-testid="profile-language"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LOCALES.map((option) => (
                <SelectItem key={option} value={option} data-testid={`profile-language-${option}`}>
                  {LOCALE_NAMES[option]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("profile.passwordTitle")}</CardTitle>
          <CardDescription>{t("profile.passwordHint")}</CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...passwordForm}>
            <form
              data-testid="profile-password-form"
              className="space-y-4"
              onSubmit={passwordForm.handleSubmit(savePassword)}
            >
              <FormField
                control={passwordForm.control}
                name="oldPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("profile.oldPassword")}</FormLabel>
                    <FormControl>
                      <Input type="password" autoComplete="current-password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={passwordForm.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("profile.newPassword")}</FormLabel>
                    <FormControl>
                      <Input type="password" autoComplete="new-password" {...field} />
                    </FormControl>
                    <FormDescription>{t("validation.password")}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={passwordForm.control}
                name="passwordConfirm"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("profile.confirmPassword")}</FormLabel>
                    <FormControl>
                      <Input type="password" autoComplete="new-password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button type="submit" disabled={savingPassword}>
                {t("profile.savePassword")}
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>

      <TwoFactorSettings />

      <DeleteAccountCard />
    </div>
  );
}
